import { Flex, Form, Typography } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"

const { Text } = Typography

interface FormPasswordStrengthIndicatorProps {
  name: string
  showHint?: boolean
  onValidityChange?: (value: boolean) => void
}

const FormPasswordStrengthIndicator = ({
  name,
  onValidityChange,
  showHint = true
}: FormPasswordStrengthIndicatorProps) => {
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const item = Form.Item.useStatus()
  const value = Form.useWatch(name, form)

  const fieldError = item?.errors
  const hasError = fieldError && fieldError.length > 0
  const errorMessage = hasError ? fieldError[0] : null

  const patterns = {
    strong:
      /^(?=.*[a-zA-Z])(?=.*[±!@#$%^&*()_+=;:'"|?/.,><`~])(?=.*[0-9]).{8,}$/,
    good1: /^(?=.*[a-zA-Z])(?=.*[0-9]).{8,}$/,
    good2: /^(?=.*[a-zA-Z])(?=.*[±!@#$%^&*()_+=;:'"|?/.,><`~]).{8,}$/
  }

  const getPasswordStrength = (password: string) => {
    if (!password) {
      return { status: "", isValid: false }
    }

    const length = password.length

    if (length < 8) {
      return { status: "Weak", isValid: false }
    }

    if (patterns.strong.test(password)) {
      return { status: "Strong", isValid: true }
    }

    if (patterns.good1.test(password) || patterns.good2.test(password)) {
      return { status: "Good", isValid: true }
    }

    return { status: "Fair", isValid: true }
  }

  const strength = getPasswordStrength(value)
  const { barWidth, textColor, barColor } = getStrengthData(strength?.status)

  useEffect(() => {
    onValidityChange?.(strength.isValid && !hasError)
  }, [strength.isValid, hasError, onValidityChange])

  if (hasError && errorMessage) {
    return (
      <div className={`pt-1.5`}>
        <Text type={"danger"}>{errorMessage}</Text>
      </div>
    )
  }

  return (
    <div className={"min-h-[44px]"}>
      {value ? (
        <Flex gap={"small"} align={"center"}>
          <Text className={`font-bold uppercase ${textColor}`}>
            {t(strength.status)}
          </Text>
          <div className="h-2 flex-1 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ease-in-out rounded-full ${barColor}`}
              style={{ width: barWidth }}
            />
          </div>
        </Flex>
      ) : (
        showHint && (
          <Text type={"secondary"}>
            {t(
              "Use at least 8 characters, with mix of characters, numbers and letters."
            )}
          </Text>
        )
      )}
    </div>
  )
}

const getStrengthData = (status: string) => {
  const strengthConfig = {
    Weak: {
      barWidth: "25%",
      textColor: "text-red-500",
      barColor: "bg-red-500"
    },
    Fair: {
      barWidth: "50%",
      textColor: "text-orange-500",
      barColor: "bg-orange-500"
    },
    Good: {
      barWidth: "75%",
      textColor: "text-yellow-600",
      barColor: "bg-yellow-500"
    },
    Strong: {
      barWidth: "100%",
      textColor: "text-green-600",
      barColor: "bg-green-500"
    }
  }

  return (
    strengthConfig[status as keyof typeof strengthConfig] ?? {
      barWidth: "0%",
      textColor: "text-gray-500",
      barColor: "bg-gray-300"
    }
  )
}

export default FormPasswordStrengthIndicator
